import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { useChat } from '@/contexts/ChatContext';
import { messageCampaignFeedApi } from '@/lib/api/services/messageCampaignFeed';
import type { CampaignButton } from '@/lib/admin/messageCampaigns';
import { isInternalUrl, pickActiveCampaign, type LiveCampaign } from '@/lib/campaigns/portalCampaigns';
import { isTopBannerRoute } from '@/components/notifications/InAppAnnouncementHost';

export const PORTAL_CAMPAIGNS_KEY = ['portal-campaigns'] as const;

export const MESSAGE_CAMPAIGN_EVENT = 'message_campaign';

const CAMPAIGN_POLL_MS = 5 * 60_000;

export function usePortalCampaigns() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  const enabled = !!user && !pathname.startsWith('/admin');

  const { data, refetch } = useQuery({
    queryKey: PORTAL_CAMPAIGNS_KEY,
    queryFn: async () => {
      const res = await messageCampaignFeedApi.live('portal');
      if (res.error) return [] as LiveCampaign[];
      return res.data;
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: enabled ? CAMPAIGN_POLL_MS : false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const wasEnabledRef = useRef(enabled);
  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;
    if (enabled && !wasEnabled) void refetch();
  }, [enabled, refetch]);

  const active = useMemo(
    () => pickActiveCampaign(data || [], { canRenderBanner: isTopBannerRoute(pathname) }),
    [data, pathname],
  );

  const chat = useChat();
  const socket = chat?.socket ?? null;
  useEffect(() => {
    if (!enabled || !socket) return;
    const onCampaign = () => {
      void queryClient.invalidateQueries({ queryKey: PORTAL_CAMPAIGNS_KEY });
    };
    socket.on(MESSAGE_CAMPAIGN_EVENT, onCampaign);
    return () => {
      socket.off(MESSAGE_CAMPAIGN_EVENT, onCampaign);
    };
  }, [enabled, socket, queryClient]);

  const dismiss = useCallback(
    (campaign: LiveCampaign) => {
      queryClient.setQueryData<LiveCampaign[]>(PORTAL_CAMPAIGNS_KEY, (old) =>
        (old || []).filter((row) => row.id !== campaign.id),
      );
      void messageCampaignFeedApi.dismiss(campaign.id);
    },
    [queryClient],
  );

  const activate = useCallback(
    (campaign: LiveCampaign, button: CampaignButton) => {
      void messageCampaignFeedApi.click(campaign.id);
      if (isInternalUrl(button.url)) {
        navigate(button.url);
      } else {
        window.open(button.url, '_blank', 'noopener,noreferrer');
      }
      dismiss(campaign);
    },
    [dismiss, navigate],
  );

  const seenRef = useRef<Set<string>>(new Set());
  const markSeen = useCallback((campaign: LiveCampaign | null | undefined) => {
    if (!campaign || seenRef.current.has(campaign.id)) return;
    seenRef.current.add(campaign.id);
    void messageCampaignFeedApi.impression(campaign.id);
  }, []);

  const playedRef = useRef<Set<string>>(new Set());
  const markVideoPlayed = useCallback((campaign: LiveCampaign | null | undefined) => {
    if (!campaign || playedRef.current.has(campaign.id)) return;
    playedRef.current.add(campaign.id);
    void messageCampaignFeedApi.videoPlay(campaign.id);
  }, []);

  return { enabled, active, dismiss, activate, markSeen, markVideoPlayed };
}
